import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { cciHistoryService, CCIRecord, CCIRecordDetail } from '../Services/CCIHistoryService';

type SortField = 'createdAt' | 'vendor.name' | 'shipmentDetails.otherReferences' | 'totalValue';
type SortDirection = 'asc' | 'desc';

export const CCIHistory: React.FC = () => {
    const [cciRecords, setCCIRecords] = useState<CCIRecord[]>([]);
    const [selectedRecord, setSelectedRecord] = useState<CCIRecordDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDownloading, setIsDownloading] = useState(false);

    // Sorting state
    const [sortField, setSortField] = useState<SortField>('createdAt');
    const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

    useEffect(() => {
        fetchCCIHistory();
    }, []);

    const fetchCCIHistory = async () => {
        setLoading(true);
        try {
            const records = await cciHistoryService.getHistory();
            setCCIRecords(records);
            setError(null);
        } catch (err) {
            console.error("Error fetching CCI history:", err);
            setError("Failed to load CCI history. Please try again later.");
        } finally {
            setLoading(false);
        }
    };

    const fetchCCIDetail = async (id: string) => {
        try {
            const record = await cciHistoryService.getRecordById(id);
            setSelectedRecord(record);
        } catch (err) {
            console.error("Error fetching CCI detail:", err);
            setError("Failed to load CCI details. Please try again later.");
        }
    };

    const downloadPDF = async (id: string) => {
        setIsDownloading(true);
        try {
            const blob = await cciHistoryService.downloadPDF(id);

            // Create a download link for the PDF
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `canada_customs_invoice_${id}.pdf`;
            document.body.appendChild(a);
            a.click();

            // Cleanup
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (err) {
            console.error("Error downloading PDF:", err);
            setError("Failed to download PDF. Please try again later.");
        } finally {
            setIsDownloading(false);
        }
    };

    const formatDate = (dateString: string) => {
        try {
            return format(new Date(dateString), 'MMM d, yyyy');
        } catch (e) {
            return 'Invalid date';
        }
    };

    const formatCurrency = (value: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency || 'USD',
        }).format(value);
    };

    // Handle sort when a column header is clicked
    const handleSort = (field: SortField) => {
        // If clicking the same field, toggle direction
        if (field === sortField) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            // If clicking a new field, set it as the sort field and default to ascending
            setSortField(field);
            setSortDirection('asc');
        }
    };

    // Get the sorted records
    const getSortedRecords = () => {
        // Create a copy of the records to sort
        const recordsCopy = [...cciRecords];

        return recordsCopy.sort((a, b) => {
            let valueA, valueB;

            // Get the values to compare based on the sort field
            if (sortField === 'vendor.name') {
                valueA = a.vendor.name.toLowerCase();
                valueB = b.vendor.name.toLowerCase();
            } else if (sortField === 'shipmentDetails.otherReferences') {
                valueA = (a.shipmentDetails.otherReferences || '').toLowerCase();
                valueB = (b.shipmentDetails.otherReferences || '').toLowerCase();
            } else if (sortField === 'totalValue') {
                valueA = a.totalValue;
                valueB = b.totalValue;
            } else {
                // Default to date sorting
                valueA = new Date(a.createdAt).getTime();
                valueB = new Date(b.createdAt).getTime();
            }

            // Determine sort order
            if (sortDirection === 'asc') {
                return valueA > valueB ? 1 : -1;
            } else {
                return valueA < valueB ? 1 : -1;
            }
        });
    };

    // Get sort indicator arrow
    const getSortIndicator = (field: SortField) => {
        if (sortField !== field) return null;

        return (
            <span className="ml-1">
                {sortDirection === 'asc' ? '▲' : '▼'}
            </span>
        );
    };

    return (
        <div className="p-3 sm:p-6">
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Canada Customs Invoice History</h2>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 sm:px-4 sm:py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {loading ? (
                <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-4 text-center">
                    Loading CCI history...
                </div>
            ) : (
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="w-full md:w-1/2 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                        <h3 className="text-lg font-medium mb-3">Your Invoices</h3>

                        {cciRecords.length === 0 ? (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                No invoices found. Create your first Canada Customs Invoice to see it here.
                            </p>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                    <thead>
                                        <tr>
                                            <th
                                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none"
                                                onClick={() => handleSort('createdAt')}
                                            >
                                                Date {getSortIndicator('createdAt')}
                                            </th>
                                            <th
                                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none"
                                                onClick={() => handleSort('vendor.name')}
                                            >
                                                Vendor {getSortIndicator('vendor.name')}
                                            </th>
                                            <th
                                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none"
                                                onClick={() => handleSort('shipmentDetails.otherReferences')}
                                            >
                                                References {getSortIndicator('shipmentDetails.otherReferences')}
                                            </th>
                                            <th
                                                className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 select-none"
                                                onClick={() => handleSort('totalValue')}
                                            >
                                                Value {getSortIndicator('totalValue')}
                                            </th>
                                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider select-none">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                        {getSortedRecords().map((record) => (
                                            <tr
                                                key={record._id}
                                                className={`hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer ${selectedRecord?._id === record._id ? 'bg-blue-50 dark:bg-blue-900' : ''}`}
                                                onClick={() => fetchCCIDetail(record._id)}
                                            >
                                                <td className="px-3 py-2 text-sm">{formatDate(record.createdAt)}</td>
                                                <td className="px-3 py-2 text-sm">{record.vendor.name}</td>
                                                <td className="px-3 py-2 text-sm">{record.shipmentDetails.otherReferences || 'N/A'}</td>
                                                <td className="px-3 py-2 text-sm">{formatCurrency(record.totalValue, record.currency)}</td>
                                                <td className="px-3 py-2 text-sm">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            downloadPDF(record._id);
                                                        }}
                                                        className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                                                        disabled={isDownloading}
                                                    >
                                                        {isDownloading ? 'Downloading...' : 'Download'}
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    <div className="w-full md:w-1/2 bg-white dark:bg-gray-800 shadow rounded-lg p-4">
                        <h3 className="text-lg font-medium mb-3">Invoice Details</h3>

                        {!selectedRecord ? (
                            <p className="text-gray-500 dark:text-gray-400 text-center py-4">
                                Select an invoice to view details
                            </p>
                        ) : (
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Invoice Date</h4>
                                        <p className="text-sm">{formatDate(selectedRecord.createdAt)}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Shipment Date</h4>
                                        <p className="text-sm">{formatDate(selectedRecord.dateOfDirectShipment)}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Vendor</h4>
                                        <p className="text-sm">{selectedRecord.vendor.name}</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {selectedRecord.vendor.address}, {selectedRecord.vendor.city}, {selectedRecord.vendor.stateProvince} {selectedRecord.vendor.postalCode}, {selectedRecord.vendor.country}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Consignee</h4>
                                        <p className="text-sm">{selectedRecord.consignee.name}</p>
                                        <p className="text-sm text-gray-500 dark:text-gray-400">
                                            {selectedRecord.consignee.address}, {selectedRecord.consignee.city}, {selectedRecord.consignee.stateProvince} {selectedRecord.consignee.postalCode}, {selectedRecord.consignee.country}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold mb-1">Purchaser</h4>
                                    <p className="text-sm">{selectedRecord.purchaser.name}</p>
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {selectedRecord.purchaser.address}, {selectedRecord.purchaser.city}, {selectedRecord.purchaser.stateProvince} {selectedRecord.purchaser.postalCode}, {selectedRecord.purchaser.country}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Transportation</h4>
                                        <p className="text-sm">{selectedRecord.shipmentDetails.transportation || 'N/A'}</p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Conditions of Sale</h4>
                                        <p className="text-sm">{selectedRecord.shipmentDetails.conditionsOfSale || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Country of Transhipment</h4>
                                        <p className="text-sm">{selectedRecord.shipmentDetails.countryOfTransshipment || 'N/A'}</p>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Net Weight</h4>
                                        <p className="text-sm">
                                            {selectedRecord.shipmentDetails.netWeight
                                                ? `${selectedRecord.shipmentDetails.netWeight} ${selectedRecord.shipmentDetails.weightUnit}`
                                                : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Gross Weight</h4>
                                        <p className="text-sm">
                                            {selectedRecord.shipmentDetails.grossWeight
                                                ? `${selectedRecord.shipmentDetails.grossWeight} ${selectedRecord.shipmentDetails.weightUnit}`
                                                : 'N/A'}
                                        </p>
                                    </div>
                                    <div>
                                        <h4 className="text-sm font-semibold mb-1">Total Value</h4>
                                        <p className="text-sm font-bold">{formatCurrency(selectedRecord.totalValue, selectedRecord.currency)}</p>
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-sm font-semibold mb-1">Items ({selectedRecord.items.length})</h4>
                                    <div className="overflow-x-auto mt-2">
                                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                                            <thead>
                                                <tr>
                                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Part #</th>
                                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Description</th>
                                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">HS Code</th>
                                                    <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Origin</th>
                                                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Qty</th>
                                                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Unit Price</th>
                                                    <th className="px-2 py-1 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                                                {selectedRecord.items.map((item, index) => (
                                                    <tr key={index}>
                                                        <td className="px-2 py-1 text-xs">{item.partNumber}</td>
                                                        <td className="px-2 py-1 text-xs">{item.description}</td>
                                                        <td className="px-2 py-1 text-xs">{item.hsCode || 'N/A'}</td>
                                                        <td className="px-2 py-1 text-xs">{item.countryOfOrigin || 'N/A'}</td>
                                                        <td className="px-2 py-1 text-xs text-right">{item.quantity}</td>
                                                        <td className="px-2 py-1 text-xs text-right">{item.unitPrice.toFixed(2)}</td>
                                                        <td className="px-2 py-1 text-xs text-right">{(item.quantity * item.unitPrice).toFixed(2)}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                <div className="mt-4 flex justify-end">
                                    <button
                                        onClick={() => downloadPDF(selectedRecord._id)}
                                        className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                        disabled={isDownloading}
                                    >
                                        {isDownloading ? 'Downloading...' : 'Download PDF'}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
